// src/components/settings/SettingsPage.tsx
import React from 'react'
import { Routes, Route, Link, useLocation } from 'react-router-dom'
import {
  User,
  Building,
  Users,
  UserCog,
  Bell,
  Shield,
  HelpCircle,
  FileText,
  Network,
  Contact,
  Workflow,
  MailWarning,
} from 'lucide-react'
import { MyProfile } from '../settings/MyProfile'
import { UserManagement } from '../settings/UserManagement'
import { TeamManagement } from '../settings/TeamManagement'
import { RegionManagement } from '../settings/RegionManagement'
import { RepRoster } from '../settings/RepRoster'
import { RoutingRulesManagement } from '../settings/RoutingRulesManagement'
import { FailureView } from '../settings/FailureView'
import { ChangePassword } from '../settings/ChangePassword'
import { Avatar } from '../ui/Avatar'
import { useAuthStore } from '../../stores/authStore'

const CompanyInformation = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-bold text-gray-900 mb-4">
      Company Information
    </h2>
    <p className="text-gray-600">Company settings functionality coming soon.</p>
  </div>
)

const Notification = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Notifications</h2>
    <p className="text-gray-600">
      Notification settings functionality coming soon.
    </p>
  </div>
)

const HelpAndSupport = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Help and Support</h2>
    <p className="text-gray-600">Help and support resources coming soon.</p>
  </div>
)

const PrivacyPolicy = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Privacy Policy</h2>
    <p className="text-gray-600">Privacy policy information coming soon.</p>
  </div>
)

const TermsOfServices = () => (
  <div className="bg-white shadow rounded-lg p-6">
    <h2 className="text-xl font-bold text-gray-900 mb-4">Terms of Services</h2>
    <p className="text-gray-600">Terms of service information coming soon.</p>
  </div>
)

export const SettingsPage: React.FC = () => {
  const location = useLocation()
  const { user } = useAuthStore()

  // Determine if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'

  const settingsNavigation = [
    {
      name: 'My Profile',
      href: '/settings/profile',
      icon: User,
      current: location.pathname === '/settings/profile',
      description: 'Manage your personal information',
    },
    {
      name: 'Change Password',
      href: '/settings/password',
      icon: Shield,
      current: location.pathname === '/settings/password',
      description: 'Update your login credentials',
    },
    {
      name: 'Users',
      href: '/settings/users',
      icon: Users,
      current: location.pathname === '/settings/users',
      description: 'Manage roles and access for your team',
      adminOnly: true,
    },
    {
      name: 'Team',
      href: '/settings/team',
      icon: UserCog,
      current: location.pathname === '/settings/team',
      description: 'Market-access users, positions and attributes',
      adminOnly: true,
    },
    {
      name: 'Regions',
      href: '/settings/regions',
      icon: Network,
      current: location.pathname === '/settings/regions',
      description: 'Build and maintain your region hierarchy',
      adminOnly: true,
    },
    {
      name: 'Rep roster',
      href: '/settings/reps',
      icon: Contact,
      current: location.pathname === '/settings/reps',
      description: 'Register your field force by CSV',
      adminOnly: true,
    },
    {
      name: 'Routing Rules',
      href: '/settings/routing-rules',
      icon: Workflow,
      current: location.pathname === '/settings/routing-rules',
      description: 'Assignment and escalation paths per question type',
      adminOnly: true,
    },
    {
      name: 'Failures',
      href: '/settings/failures',
      icon: MailWarning,
      current: location.pathname === '/settings/failures',
      description: 'Bounces, dead letters and delivery problems',
      adminOnly: true,
    },
    {
      name: 'Company Information',
      href: '/settings/company',
      icon: Building,
      current: location.pathname === '/settings/company',
      description: 'Organization details and settings',
      adminOnly: true,
    },
    {
      name: 'Notification',
      href: '/settings/notifications',
      icon: Bell,
      current: location.pathname === '/settings/notifications',
      description: 'Manage notification preferences',
    },
    {
      name: 'Help and Support',
      href: '/settings/help',
      icon: HelpCircle,
      current: location.pathname === '/settings/help',
      description: 'Get help and contact support',
    },
    {
      name: 'Privacy Policy',
      href: '/settings/privacy',
      icon: Shield,
      current: location.pathname === '/settings/privacy',
      description: 'View our privacy policy',
    },
    {
      name: 'Terms of Services',
      href: '/settings/terms',
      icon: FileText,
      current: location.pathname === '/settings/terms',
      description: 'View terms of service',
    },
  ]

  // Filter navigation based on user role
  const filteredNavigation = settingsNavigation.filter(
    item => !item.adminOnly || isAdmin
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <p className="mt-2 text-gray-600">
            Manage your account settings and preferences
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Settings Navigation Sidebar */}
          <div className="lg:col-span-1">
            <nav className="space-y-1">
              {filteredNavigation.map(item => {
                const Icon = item.icon
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`
                      group flex items-start p-3 rounded-lg transition-colors duration-200
                      ${
                        item.current
                          ? 'bg-brand-50 border-l-4 border-brand-500 text-brand-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon
                      className={`
                        flex-shrink-0 w-5 h-5 mt-0.5 mr-3
                        ${item.current ? 'text-brand-500' : 'text-gray-400 group-hover:text-gray-600'}
                      `}
                    />
                    <div>
                      <div
                        className={`text-sm font-medium ${item.current ? 'text-brand-700' : 'text-gray-900'}`}
                      >
                        {item.name}
                      </div>
                      <div
                        className={`text-xs mt-1 ${item.current ? 'text-brand-600' : 'text-gray-500'}`}
                      >
                        {item.description}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </nav>

            {/* User Info Card */}
            <div className="mt-8 bg-white rounded-lg shadow p-4">
              <div className="flex items-center">
                <Avatar user={user || undefined} size="md" />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.first_name && user?.last_name
                      ? `${user.first_name} ${user.last_name}`
                      : user?.email?.split('@')[0] || 'User'}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {user?.role?.replace('_', ' ') || 'User'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            <Routes>
              {/* Default route - redirect to profile */}
              <Route index element={<MyProfile />} />

              {/* Settings pages */}
              <Route path="profile" element={<MyProfile />} />
              <Route path="password" element={<ChangePassword />} />

              {/* Admin-only routes */}
              {isAdmin && (
                <>
                  <Route path="users" element={<UserManagement />} />
                  <Route path="team" element={<TeamManagement />} />
                  <Route path="regions" element={<RegionManagement />} />
                  <Route path="reps" element={<RepRoster />} />
                  <Route
                    path="routing-rules"
                    element={<RoutingRulesManagement />}
                  />
                  <Route path="failures" element={<FailureView />} />
                  <Route path="company" element={<CompanyInformation />} />
                </>
              )}

              {/* General routes */}
              <Route path="notifications" element={<Notification />} />
              <Route path="help" element={<HelpAndSupport />} />
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="terms" element={<TermsOfServices />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  )
}
